/* Copyable values to type into the device / server, per connection method. */
import { useState } from "react";
import type { ConnectionInfo, Device } from "../../../data/types";
import { useTestDevice } from "../../../data/hooks";
import { Alert, Button, CopyValue, Icon, errMsg, useToast } from "../../../ui";
import { useI18n } from "../../../i18n";

export const isMasked = (t: string | null | undefined) => !t || t.startsWith("••••");

export function originOf(info: ConnectionInfo): string {
  try { return new URL(info.webhookBase).origin; } catch { return (info.https ? "https://" : "http://") + info.pushHost; }
}
export const webhookUrl = (info: ConnectionInfo, d: Pick<Device, "id">) => info.webhookBase + d.id;
export const agentCommand = (info: ConnectionInfo, d: Pick<Device, "id">, token: string) => `starthr-agent --server ${originOf(info)} --device ${d.id} --token ${token}`;
export const tzText = (n: number) => (n >= 0 ? "+" : "−") + Math.abs(n);

function TokenRow({ token }: { token: string }) {
  const { L } = useI18n();
  if (isMasked(token)) {
    return (
      <div className="copyrow" data-testid="token-masked">
        <span className="copy-l">{L("Device token", "رمز الجهاز")}</span>
        <code className="copy-v secret" dir="ltr">{token || "••••"}</code>
        <span className="xs muted">{L("Hidden — rotate to get a new one", "مخفي — جدّد الرمز للحصول على رمز جديد")}</span>
      </div>
    );
  }
  return (
    <div>
      <CopyValue label={L("Device token", "رمز الجهاز")} value={token} testId="val-token" />
      <div className="token-warn" role="status" data-testid="token-warning"><Icon name="alert" className="s" />{L("Copy now — it will be hidden after you leave this page.", "انسخه الآن — سيتم إخفاؤه بعد مغادرة هذه الصفحة.")}</div>
    </div>
  );
}

export function ValuesPanel({ device, info, token, onTestResult }: { device: Device; info: ConnectionInfo; token?: string | null; onTestResult?: (ok: boolean) => void }) {
  const { L } = useI18n();
  const toast = useToast();
  const test = useTestDevice();
  const [res, setRes] = useState<{ ok: boolean; message: string } | null>(null);
  const tok = token ?? device.webhookToken;
  const runTest = () => test.mutate(device.id, {
    onSuccess: (r) => { setRes(r); onTestResult?.(r.ok); },
    onError: (e) => toast(errMsg(e), "bad"),
  });

  if (device.protocol === "push") {
    return (
      <div data-testid="values-panel">
        <CopyValue label={L("Server address", "عنوان الخادم")} value={info.pushHost} testId="val-host" />
        <CopyValue label={L("Server port", "منفذ الخادم")} value={String(info.pushPort)} testId="val-port" />
        <CopyValue label="HTTPS" value={info.https ? L("On", "مفعّل") : L("Off", "غير مفعّل")} testId="val-https" />
        <CopyValue label={L("Enable domain name", "تفعيل اسم النطاق")} value={L("On", "مفعّل")} testId="val-domain" />
        <CopyValue label={L("Time zone", "المنطقة الزمنية")} value={tzText(info.timezoneOffset)} testId="val-tz" />
        <CopyValue label={L("Serial number (check)", "الرقم التسلسلي (للتحقق)")} value={device.serial} testId="val-serial" />
      </div>
    );
  }
  if (device.protocol === "webhook") {
    const sample = JSON.stringify({ deviceUserId: "1001", time: "2026-10-03T07:41:09+03:00", direction: "in", verify: "face" }, null, 2);
    return (
      <div data-testid="values-panel">
        <CopyValue label={L("Webhook URL", "رابط Webhook")} value={webhookUrl(info, device)} testId="val-url" />
        <CopyValue label={L("Header name", "اسم الترويسة")} value="X-Device-Token" testId="val-header" />
        <TokenRow token={tok} />
        <details className="mt" data-testid="sample-body">
          <summary className="sm" style={{ cursor: "pointer" }}>{L("Sample JSON body (one punch, or an array)", "مثال على محتوى JSON (حركة واحدة أو مصفوفة)")}</summary>
          <pre className="codeblock mt" dir="ltr">{sample}</pre>
          <p className="xs muted">{L("Hikvision AccessControllerEvent, BioStar and BioTime event formats are recognised automatically. CSV text also works.", "تُقرأ صيغ أحداث Hikvision وBioStar وBioTime تلقائيًا، كما يُقبل نص CSV.")}</p>
        </details>
      </div>
    );
  }
  if (device.protocol === "agent") {
    const dl = info.agentDownloadUrl.startsWith("http") ? info.agentDownloadUrl : originOf(info) + info.agentDownloadUrl;
    return (
      <div data-testid="values-panel">
        <div className="copyrow"><span className="copy-l">{L("Download", "التنزيل")}</span><a className="copy-v" href={dl} target="_blank" rel="noreferrer" dir="ltr" data-testid="agent-download">{L("StartHR Agent for Windows / Linux / Raspberry Pi", "وكيل ستارت HR لويندوز / لينكس / Raspberry Pi")}</a></div>
        <CopyValue label={L("Device ID", "معرّف الجهاز")} value={device.id} testId="val-device-id" />
        <TokenRow token={tok} />
        {!isMasked(tok) && <CopyValue label={L("Command", "الأمر")} value={agentCommand(info, device, tok)} testId="val-command" />}
      </div>
    );
  }
  if (device.protocol === "poll") {
    return (
      <div data-testid="values-panel" className="stack">
        <p className="sm" style={{ margin: 0 }}>{L("StartHR will connect to", "سيتصل ستارت HR بـ")} <code dir="ltr" data-testid="poll-url">{device.baseUrl || "—"}</code> {L(`every ${Math.round(device.pollIntervalSec / 60)} min`, `كل ${Math.round(device.pollIntervalSec / 60)} دقيقة`)}</p>
        <div><Button icon="refresh" onClick={runTest} loading={test.isPending} data-testid="btn-test-connection">{L("Test connection", "اختبار الاتصال")}</Button></div>
        {res && <Alert tone={res.ok ? "ok" : "bad"} title={res.ok ? L("Connected", "تم الاتصال") : L("Couldn't connect", "تعذّر الاتصال")} desc={res.message} testId="test-result" />}
      </div>
    );
  }
  return (
    <div data-testid="values-panel">
      <p className="sm muted" style={{ margin: 0 }}>{L("No device settings needed — upload exported files whenever you like.", "لا يلزم أي إعداد في الجهاز — ارفع الملفات المصدّرة متى شئت.")}</p>
    </div>
  );
}
